/* global GMAPS_API_KEY, google */
/*
 * PRD-037 — Autocompletado de direccion del portal del partner (app SEPARADA).
 * Reusa el mapa canónico compartido (<CommerceAddressMap>: pin azul + buscador superpuesto) con el
 * mismo loader de Google Places que el panel (useJsApiLoader + GMAPS_API_KEY, libreria 'places').
 * Degrada a input de texto libre si la API no carga (sin coords, no bloquea el alta).
 */
import { useJsApiLoader } from '@react-google-maps/api';
import type { Libraries } from '@interfaces/Map';
import { useDesktop } from '@utils/hooks/useDesktop';
import { TextInputV4 } from '@components/v4/Input/Input';
import { CommerceAddressMap } from '@components/maps/CommerceAddressMap';
import { C } from './ui';

const LIBRARIES: Libraries = ['places'];

// Fallback cuando aun no hay coordenadas: centro de Monterrey, NL.
const FALLBACK_CENTER = { lat: 25.6866, lng: -100.3161 };

export interface AddressValue {
  direccion: string;
  lat: number | null;
  lng: number | null;
}

interface Props {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
  label?: string;
}

export const AddressAutocomplete = ({ value, onChange, label = 'Direccion del negocio (opcional)' }: Props) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GMAPS_API_KEY, libraries: LIBRARIES });
  const isDesktop = useDesktop();

  const hasCoords = value.lat !== null && value.lng !== null;
  const mapCenter = hasCoords ? { lat: value.lat as number, lng: value.lng as number } : FALLBACK_CENTER;

  // Elegir una sugerencia captura el texto de la direccion + lat/lng.
  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    const loc = place.geometry?.location;
    onChange({
      direccion: place.formatted_address || value.direccion,
      lat: loc ? loc.lat() : null,
      lng: loc ? loc.lng() : null,
    });
  };

  // Arrastrar el marcador fija/refina lat/lng conservando el texto de la direccion.
  const handleMarkerDragEnd = (lat: number, lng: number) => {
    onChange({ ...value, lat, lng });
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.primary, marginBottom: 6 }}>
        {label}
      </span>

      {isLoaded ? (
        <>
          <CommerceAddressMap
            isLoaded={isLoaded}
            center={mapCenter}
            hasSavedLocation={hasCoords}
            markerPosition={mapCenter}
            onPlaceSelected={handlePlaceSelected}
            onMarkerDragEnd={handleMarkerDragEnd}
            searchPlaceholder="Escribe y elige la direccion"
            height={isDesktop ? 220 : 170}
          />
          <span style={{ display: 'block', fontSize: 11, color: C.muted, marginTop: 6 }}>
            Arrastra el marcador para fijar el punto exacto del negocio.
          </span>
        </>
      ) : (
        // Degradado: el alta no se bloquea — texto libre (sin coords) si Google Places no carga.
        <TextInputV4
          value={value.direccion}
          onChange={(v) => onChange({ direccion: String(v), lat: null, lng: null })}
          placeholder="Escribe la direccion del negocio"
        />
      )}

      {hasCoords && (
        <span style={{ display: 'block', fontSize: 11, color: C.success, marginTop: 6 }}>Ubicacion capturada ✓</span>
      )}
    </div>
  );
};
